import React from 'react';

import { IdToken, LocalStorageCache, useAuth0, User } from '@auth0/auth0-react';
import { useLocation } from '@reach/router';

import Alerts from '@components/Alerts';
import Footer from '@components/Footer';
import Header from '@components/Header';
import Loading from '@components/Loading';
import { MaintenanceMode } from '@presenters/web/pages/MaintenanceMode';

import useAuth0Hook from '../hooks/useAuth0Hook';

import { NOTIFICATIONS_WRAPPER_ID } from '@domain/notifications';

import {
  FEATURE_AUTH0_LOGIN,
  FEATURE_SITECORE_FOOTER,
  FEATURE_SITECORE_HEADER,
  isEnabled,
} from '@utils/features';
import { localizedNavigate } from '@utils/localized-navigate';

import { useAppConfig } from '@domui-hooks/appConfig';
import SitecoreFooter from '@domui-sitecore/wrappers/SitecoreFooter';
import SitecoreHeader from '@domui-sitecore/wrappers/SitecoreHeader';
import { isProtectedPath } from '@domui-utils/secureRoute';

import ErrorBoundary from '@hooks/error-boundary';
import { log } from '@hooks/logger';

import { LogLevel } from '@typings/graphql';

const Auth0Layout: React.FC = ({ children }) => {
  const {
    isAuthenticated,
    getAccessTokenSilently,
    getIdTokenClaims,
    user: auth0UserInfo,
    isLoading,
    loginWithRedirect = () => {},
    logout = () => {},
  } = useAuth0();
  const [accessToken, setAccessToken] = React.useState<string>();
  const [refreshToken, setRefreshToken] = React.useState<string | undefined>();
  const [idTokenClaims, setIdTokenClaims] = React.useState<
    IdToken | undefined
  >();
  const [auth0User, setAuth0User] = React.useState<User | undefined>();
  const isAuth0LoginEnabled = isEnabled(FEATURE_AUTH0_LOGIN);
  const { language = 'en', isAuth0Initializing } = useAppConfig();
  const { isMaintenanceMode } = useAuth0Hook(
    accessToken || '',
    refreshToken || '',
    idTokenClaims,
    auth0User
  );

  const location = useLocation();

  React.useEffect(() => {
    log({
      level: LogLevel.Info,
      message: `##### MRC-2394 ######:: Auth0Layout isAuth0LoginEnabled ${isAuth0LoginEnabled} Auth0 isAuthenticated ${isAuthenticated}`,
    });

    if (isAuth0LoginEnabled) {
      log({
        level: LogLevel.Info,
        message: 'QQ Auth0:: accessToken Fetch Started!',
      });
      if (!isAuthenticated) {
        log({
          level: LogLevel.Info,
          message: `QQ Auth0:: User Not Authenticated Yet!`,
        });
        return; // Wait until user is authenticated
      }

      const fetchData = async () => {
        log({
          level: LogLevel.Info,
          message: `##### MRC-2394 ######:: Auth0Layout fetchData - Auth0 isAuthenticated ${isAuthenticated}`,
        });

        try {
          const accessToken = await getAccessTokenSilently();
          const idTokenClaims = await getIdTokenClaims();

          log({
            level: LogLevel.Info,
            message: `##### MRC-2394 ######:: Auth0Layout Auth0 accessToken ${accessToken}`,
          });

          log({
            level: LogLevel.Info,
            message: `##### MRC-2394 ######:: Auth0Layout Auth0 idTokenClaims ${idTokenClaims &&
              JSON.stringify(idTokenClaims)}`,
          });

          // get refresh token from auth0-spa-js local storage cache
          const auth0Cache = new LocalStorageCache();
          const key = auth0Cache
            .allKeys()
            .find(key => key.includes('auth0spa'));

          log({
            level: LogLevel.Info,
            message: `##### MRC-2394 ######:: Auth0Layout auth0spa key ${key}`,
          });

          type Auth0RefreshTokenResponse = {
            body?: {
              // eslint-disable-next-line camelcase
              refresh_token?: string;
            };
          };

          const refreshTokenValue = auth0Cache.get(key as string);
          log({
            level: LogLevel.Info,
            message: `##### MRC-2394 ######:: Auth0Layout refreshTokenValue ${refreshTokenValue}`,
          });

          const { refresh_token: refreshTokenFromStorage } =
            (refreshTokenValue as Auth0RefreshTokenResponse)?.body ?? {};
          const finalRefreshToken = refreshTokenFromStorage;

          log({
            level: LogLevel.Info,
            message: `##### MRC-2394 ######:: Auth0Layout Auth0 RefreshToken ${finalRefreshToken}`,
          });

          if (!accessToken) {
            log({
              level: LogLevel.Info,
              message: 'QQ Auth0:: accessToken not available!',
            });
            return;
          }

          // eslint-disable-next-line no-underscore-dangle
          if (!idTokenClaims?.__raw) {
            log({
              level: LogLevel.Info,
              message: 'idTokenClaims.__raw is not available!',
            });
          }

          log({
            level: LogLevel.Info,
            message: `##### MRC-2394 ######:: Auth0Layout Auth0 final accessToken ${accessToken}`,
          });
          log({
            level: LogLevel.Info,
            message: `##### MRC-2394 ######:: Auth0Layout Auth0 final RefreshToken ${finalRefreshToken}`,
          });
          log({
            level: LogLevel.Info,
            message: `##### MRC-2394 ######:: Auth0Layout Auth0 final idTokenClaims ${idTokenClaims &&
              JSON.stringify(idTokenClaims)}`,
          });
          log({
            level: LogLevel.Info,
            message: `##### MRC-2394 ######:: Auth0Layout final auth0UserInfo ${auth0UserInfo &&
              JSON.stringify(auth0UserInfo)}`,
          });
          setAccessToken(accessToken);
          setRefreshToken(finalRefreshToken);
          setIdTokenClaims(idTokenClaims);
          setAuth0User(auth0UserInfo);
        } catch (err) {
          log({
            level: LogLevel.Error,
            message: `QQ Failed to fetch user info: ${JSON.stringify(err)}`,
          });
          localStorage.clear();
          sessionStorage.clear();

          log({
            level: LogLevel.Info,
            message: `##### MRC-2394 ######:: Auth0Layout Logging Out with Error ${err}`,
          });

          await logout({ logoutParams: { returnTo: window.location.origin } });
        }
      };

      fetchData();
    }
  }, [isAuth0LoginEnabled, isAuthenticated]);

  React.useEffect(() => {
    const pathName =
      typeof window !== 'undefined' ? window.location.pathname : '/';

    log({
      level: LogLevel.Info,
      message: `##### MRC-2394 ######:: Auth0Layout pathName ${pathName}`,
    });

    log({
      level: LogLevel.Info,
      message: `##### MRC-2394 ######:: Auth0Layout auth0UserInfo Email ${auth0UserInfo?.email}`,
    });
    const loginRequired =
      isAuth0LoginEnabled && !auth0UserInfo?.email && !isLoading;

    log({
      level: LogLevel.Info,
      message: `##### MRC-2394 ######:: Auth0Layout loginRequired ${loginRequired}`,
    });
    log({
      level: LogLevel.Info,
      message: `##### MRC-2394 ######:: Auth0Layout isProtectedPath ${isProtectedPath(
        pathName,
        language
      )}`,
    });
    if (isProtectedPath(pathName, language) && loginRequired) {
      log({
        level: LogLevel.Info,
        message: `##### MRC-2394 ######:: Auth0Layout loginWithRedirect trying to trigger! appState ${location?.pathname +
          location?.search} ui_locales ${language}`,
      });

      // Commenting login redirect for now as its invoking Auth0 authorize call during impersonation which leads to login page.

      // loginWithRedirect({
      //   authorizationParams: {
      //     ui_locales: language,
      //   },
      //   appState: { returnTo: location.pathname + location.search },
      // });
    }
  }, [isLoading, auth0UserInfo?.email, language]);

  React.useEffect(() => {
    if (isAuth0LoginEnabled && isAuthenticated && !isLoading) {
      const segments = location.pathname.split('/').filter(Boolean);

      const isLoginPage = segments[segments.length - 1] === 'login';

      const destination = window.localStorage.getItem('destination');

      log({
        level: LogLevel.Info,
        message: `##### MRC-2394 ######:: Auth0Layout segments ${segments} isLoginPage ${isLoginPage} destination ${destination}`,
      });

      if (destination) {
        log({
          level: LogLevel.Info,
          message: `##### MRC-2394 ######:: Auth0Layout Redirecting at destination ${destination}`,
        });

        window.location.href = destination;
      } else {
        log({
          level: LogLevel.Info,
          message: `##### MRC-2394 ######:: Auth0Layout Navigating at destination ${isLoginPage} '/'`,
        });
        isLoginPage && localizedNavigate('/');
      }
    }
  }, [isAuth0LoginEnabled, isAuthenticated, isLoading]);

  if (isMaintenanceMode) {
    return <MaintenanceMode />;
  }

  if (
    isAuth0LoginEnabled &&
    (isLoading ||
      (isAuthenticated && (!accessToken || !idTokenClaims)) ||
      (isAuthenticated && isAuth0Initializing))
  ) {
    return (
      <div className="fixed inset-0 flex justify-center bg-white z-50">
        <Loading />
      </div>
    );
  }

  return isAuth0LoginEnabled ? (
    <div>
      <ErrorBoundary>{children}</ErrorBoundary>
    </div>
  ) : (
    <div className="mainContainer min-h-full">
      {isEnabled(FEATURE_SITECORE_HEADER) ? <SitecoreHeader /> : <Header />}
      <Alerts />
      <div id={NOTIFICATIONS_WRAPPER_ID} />
      <main className="flex-1 tablet:pt-1">
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>
      {isEnabled(FEATURE_SITECORE_FOOTER) ? <SitecoreFooter /> : <Footer />}
    </div>
  );
};

export default Auth0Layout;
